import React, { useMemo } from 'react';
import { SearchBar } from 'components/elements';
import { Tag, TreeSelect } from 'antd';
import type { CategorySearchOption } from 'utils/categorySearchOptions';
import {
  SearchToolbarShell,
  SearchFilterRow,
  ToolbarSearchSlot,
  FilterToolbarWrapper,
  FilterByCheckboxWrapper,
  CategoryTreeSelectGlobalStyle,
  CATEGORY_TREE_SELECT_POPUP_CLASS,
  CategoryTreeLabel,
} from './index.styled';

type CategoryTreeDataNode = {
  value: string;
  key: string;
  title: React.ReactNode;
  searchLabel: string;
  children?: CategoryTreeDataNode[];
};

type SearchOption = CategorySearchOption;

interface SearchSidebarProp {
  onSearch?: (value: string) => void;
  filterByCategories?: {
    key: string;
    title: string;
    options: SearchOption[];
    onChange: (value: { key: string; value: string }[]) => void;
    defaultValue?: string[];
  }[];
  searchDefaultValue: string;
}

function CategoryTreeLabelContent({ label, translatedName }: { label: string; translatedName?: string }) {
  return (
    <CategoryTreeLabel className="category-tree-label">
      <span className="title">{label}</span>
      {translatedName ? <span className="gloss"> {translatedName}</span> : null}
    </CategoryTreeLabel>
  );
}

function buildCategoryTreeData(options: SearchOption[]): CategoryTreeDataNode[] {
  const buildBranch = (parentId: string): CategoryTreeDataNode[] =>
    options
      .filter((option) => option.parentId === parentId)
      .sort((a, b) => a.order - b.order)
      .map((option) => {
        const children = buildBranch(option.value);
        return {
          value: option.value,
          key: option.value,
          title: <CategoryTreeLabelContent label={option.label} translatedName={option.translatedName} />,
          searchLabel: [option.label, option.translatedName].filter(Boolean).join(' ').trim(),
          ...(children.length ? { children } : {}),
        };
      });

  return buildBranch('');
}

const renderCategoryFilters = (
  filterByCategories: SearchSidebarProp['filterByCategories'],
  optionsByValue: Record<string, SearchOption>,
) => {
  if (!filterByCategories?.length) {
    return null;
  }
  return filterByCategories.map((filterByCategory) => {
    const treeData = buildCategoryTreeData(filterByCategory.options);

    return (
      <div key={filterByCategory.key} style={{ width: '100%' }}>
        <TreeSelect
          className="category-tree-select"
          popupClassName={CATEGORY_TREE_SELECT_POPUP_CLASS}
          showSearch
          style={{ width: '100%', minWidth: 180 }}
          value={filterByCategory.defaultValue}
          dropdownStyle={{ maxHeight: 400, overflow: 'auto', borderRadius: '10px' }}
          placeholder="Search by Category"
          allowClear
          multiple
          treeDefaultExpandAll
          treeData={treeData}
          treeNodeFilterProp="searchLabel"
          onChange={(value) => filterByCategory.onChange(value as any)}
          tagRender={({ value, closable, onClose }) => {
            const option = optionsByValue[String(value)];
            if (!option) {
              return <Tag closable={closable} onClose={onClose}>{String(value)}</Tag>;
            }
            return (
              <Tag closable={closable} onClose={onClose}>
                <CategoryTreeLabel>
                  <span className="title">{option.label}</span>
                  {option.translatedName ? <span className="gloss"> {option.translatedName}</span> : null}
                </CategoryTreeLabel>
              </Tag>
            );
          }}
        />
      </div>
    );
  });
};

const SearchSidebar = ({ onSearch, filterByCategories, searchDefaultValue }: SearchSidebarProp) => {
  const hasCategoryFilter = Boolean(filterByCategories?.length);

  const optionsByValue = useMemo(() => {
    const map: Record<string, SearchOption> = {};
    filterByCategories?.forEach((filter) => {
      filter.options.forEach((option) => {
        map[option.value] = option;
      });
    });
    return map;
  }, [filterByCategories]);

  return (
    <SearchToolbarShell>
      {hasCategoryFilter ? <CategoryTreeSelectGlobalStyle /> : null}
      <SearchFilterRow>
        <ToolbarSearchSlot>
          <SearchBar onSubmit={onSearch} defaultValue={searchDefaultValue} />
        </ToolbarSearchSlot>
        <FilterToolbarWrapper>
          <FilterByCheckboxWrapper>
            {renderCategoryFilters(filterByCategories, optionsByValue)}
          </FilterByCheckboxWrapper>
        </FilterToolbarWrapper>
      </SearchFilterRow>
    </SearchToolbarShell>
  );
};

export default SearchSidebar;
